import React from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { styled, Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNewOutlined'
import Drawer from '../../control/Drawer'
import useReportFeedback from '../../hooks/map/useReportFeedback'
import useUnblockPopupDetail from '../../hooks/map/useUnblockPopupDetail'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import { ReactComponent as GridHorizontalIcon } from '../../icon/grid-2-horizontal.svg'
import SvgIcon from '@mui/material/SvgIcon'
import Scrollbar from '../../control/Scrollbar'
import PieChart from '../../control/amChart/PieChart'
import BarChart from '../../control/amChart/BarChart'
import Bar from '../../control/amChart/Bar'
import Dropdown from '../../control/Dropdown'
import ReportItemCard from '../../control/ReportItemCard'
import { SignalFeedbackEnum } from '../../typings/Enum'
import ReportItemDetailLabel from '../../control/ReportItemDetailLabel'
import { observer } from 'mobx-react-lite'
import Tooltip from '@mui/material/Tooltip'
import SplitButton from '../../control/SplitButton'
import numeral from 'numeral'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import UnblockPopupDetail from './UnblockPopupDetail'
import Dialog from '../../control/Dialog'

const FormReportFeedback = () => {
    const {
        drawer,
        getDataSort,
        YM,
        options,
        dataSort,
        sort,
        handleSortData,
        dataReportDetail,
        chartProp,
        isIos,
        openDialogError,
        handleCloseErrorDialog,
    } = useReportFeedback()
    const { openIosHelp, onClickOpenIosHelp, onClickCloseIosHelp } = useUnblockPopupDetail()
    const { t } = useTranslation()
    const classes = useStyles()

    const getColorDetail = () => {
        let signalImg = ''
        let signalDescColor = '#C0C0C0'
        switch (dataReportDetail?.signalState?.code) {
            case SignalFeedbackEnum.Normal:
                signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_green.png`
                signalDescColor = '#679A2E'
                break
            case SignalFeedbackEnum.Abnormal:
                signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_warning.png`
                signalDescColor = '#F1BC31'
                break
            case SignalFeedbackEnum.None:
                signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_red.png`
                signalDescColor = '#F15F31'
                break
            default:
                signalImg = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/map_pin_gray.png`
                signalDescColor = '#C0C0C0'
                break
        }
        return {
            signalImg,
            signalDescColor,
        }
    }

    const layoutGrid = () => (
        <React.Fragment>
            <Paper elevation={3} sx={{ width: '100%', height: '310px', backgroundColor: '#FCFCFC' }}>
                <Grid container>
                    <Grid item xs={12}>
                        <PieChart
                            chartName="pieChartdiv"
                            chartData={chartProp.dataPie}
                            dataFieldsValue="sum"
                            dataFieldsCategory="signal"
                            legend={{
                                position: 'bottom',
                            }}
                            responsive
                            titleText={t('LABEL.MAP.REPORT_FEEDBACK.RECEPTION_OVERVIEW')}
                            sxProps={{
                                m: 1,
                                width: '100%',
                                height: '300px',
                            }}
                            onClickChart={chartProp.onClickChart}
                        />
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={3} sx={{ width: '100%', height: '360px', backgroundColor: '#FCFCFC' }}>
                <BarChart
                    chartName="barChartdiv"
                    chartData={chartProp.dataBarHorizontal}
                    dataFieldsCategory="provinces"
                    titleTextHead={t('LABEL.MAP.REPORT_FEEDBACK.TOP_PROVINCE')}
                    titleText={`${t('LABEL.MAP.REPORT_FEEDBACK.SIGNAL_RECEIVED')} ${chartProp.barChartTitle}`}
                    columnColor={chartProp.barChartColor}
                    sxProps={{
                        width: '100%',
                        height: '100%',
                    }}
                />
            </Paper>

            <Paper elevation={3} sx={{ width: '100%', height: '310px', backgroundColor: '#FCFCFC' }}>
                <Bar
                    chartName="bardiv"
                    chartData={chartProp.dataBarVertical}
                    dataFieldsCategory="antennaType"
                    titleText={`${t('LABEL.MAP.REPORT_FEEDBACK.ANTENNA_TYPE')} (${numeral(
                        chartProp.dataBarVerticalSum,
                    ).format('0,0')} ${t('LABEL.MAP.REPORT_FEEDBACK.LIST')})`}
                    columnColor="#3464AC"
                    sxProps={{
                        m: 1,
                        width: '90%',
                        height: '300px',
                    }}
                />
            </Paper>
        </React.Fragment>
    )
    const layoutMulti = () => (
        <React.Fragment>
            {getDataSort().map((item, index) => {
                if (item.location) {
                    return (
                        <ReportItemCard
                            key={index}
                            item={item}
                            type={'feedback'}
                            onClickLocation={drawer.onClickViewPosition}
                        />
                    )
                }
            })}
        </React.Fragment>
    )

    const DrawerFormReportFeedback = (
        <Drawer
            isOpen={drawer.openDrawer}
            hideBackdrop
            overrideClass={{ paper: classes.drawerPaper }}
            inlineStyle={{ position: 'unset' }}
        >
            <StyledContainer>
                <StyledTitle>
                    <Link to="/">
                        <IconButton
                            aria-label="close"
                            onClick={() => {
                                drawer.setOpenDrawer(false)
                            }}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: 15,
                            }}
                            className={classes.backIcon}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Link>
                    <StyledTypography variant="body2">{t('MENU.REPORT_FEEDBACK')}</StyledTypography>
                </StyledTitle>
                <StyledContent>
                    <Stack spacing={1} sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={2}>
                            <Dropdown
                                label={t('LABEL.MAP.REPORT_COMMON.YEAR')}
                                value={YM.year}
                                options={YM.dataYear}
                                onChange={YM.onChangeYear}
                                customStyle={{ width: 150 }}
                            />
                            <Dropdown
                                label={t('LABEL.MAP.REPORT_COMMON.MONTH')}
                                value={YM.month}
                                options={YM.dataMonth}
                                onChange={YM.onChangeMonth}
                                customStyle={{ width: 150 }}
                            />
                        </Stack>
                        <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ paddingTop: '8px' }}>
                            <SplitButton
                                disabled={getDataSort().length === 0}
                                optionsDisplay={0}
                                options={options.options}
                                onClick={options.onClickOption}
                                onMenuItemClick={options.onMenuItemClick}
                            />
                            <Tooltip title={t('LABEL.MAP.REPORT_FEEDBACK.LAYOUT') ?? ''} placement="top">
                                <Paper>
                                    <IconButton
                                        sx={{ p: '10px', bgcolor: '#fff', borderRadius: '5px', width: '100%' }}
                                        onClick={() => drawer.onClickLayout(!drawer.layout)}
                                    >
                                        {drawer.layout ? (
                                            <SvgIcon component={GridHorizontalIcon} className={classes.iconColor} />
                                        ) : (
                                            <img
                                                src={`${process.env.REACT_APP_IMAGE_PATH}/assets/image/chart-svgrepo-com.png`}
                                                alt=""
                                                width="21"
                                                height="21"
                                            />
                                        )}
                                    </IconButton>
                                </Paper>
                            </Tooltip>
                        </Stack>

                        {isIos && (
                            <Stack direction="row" alignItems="center">
                                <IconButton onClick={onClickOpenIosHelp}>
                                    <StyledIconInfo />
                                </IconButton>
                                <StyledTypoIosRemark>
                                    {'กรุณา Unblock Pop-up สำหรับผู้ใช้งาน iOS Safari'}
                                </StyledTypoIosRemark>
                            </Stack>
                        )}

                        {!drawer.layout && (
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                                <Typography>{t('LABEL.MAP.REPORT_FEEDBACK.SORT')}</Typography>
                                <Dropdown
                                    value={sort}
                                    options={dataSort}
                                    onChange={handleSortData}
                                    customStyle={{ width: 200 }}
                                />
                            </Stack>
                        )}
                    </Stack>
                </StyledContent>
                <StyledContentReport>
                    <div style={{ width: '100%', height: '100%' }}>
                        <Scrollbar
                            scrollbarTrackBorderRadius={10}
                            scrollbarThumbBorderRadius={10}
                            style={{
                                height: drawer.layout
                                    ? `calc(100% - ${isIos ? 141 : 102}px)`
                                    : `calc(100% - ${isIos ? 192 : 153}px)`,
                            }}
                        >
                            {getDataSort().length > 0 ? (
                                <Stack spacing={2} sx={{ px: 2, py: 1 }}>
                                    {drawer.layout ? layoutGrid() : layoutMulti()}
                                </Stack>
                            ) : (
                                <div style={{ width: '100%', textAlign: 'center' }}>
                                    <Typography>{t('LABEL.MAP.REPORT_COMMON.NOT_FOUND')}</Typography>
                                </div>
                            )}
                        </Scrollbar>
                    </div>
                </StyledContentReport>
            </StyledContainer>
        </Drawer>
    )

    const DrawerFormReportFeedbackDetail = (
        <Drawer
            isOpen={drawer.openDrawerDetail}
            hideBackdrop
            inlineStyle={{ position: 'unset' }}
            overrideClass={{ paper: classes.drawerPaper }}
        >
            <StyledContainer>
                <StyledTitle>
                    <Link to="/report-feedback">
                        <IconButton
                            aria-label="close"
                            onClick={() => {
                                drawer.setOpenDrawerDetail(false)
                            }}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: 15,
                            }}
                            className={classes.backIcon}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                    </Link>
                    <StyledTypography variant="h6">{t('MENU.REPORT_FEEDBACK')}</StyledTypography>
                </StyledTitle>
                <StyledContent>
                    <Stack spacing={1}>
                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.RECEIVING_SIGNAL')}
                            text={dataReportDetail?.signalState?.name}
                            color={getColorDetail().signalDescColor}
                            signalImg={getColorDetail().signalImg}
                        />

                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.ABNORMAL')}
                            text={dataReportDetail?.signalDetail}
                        />

                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.RECEPTION')}
                            text={dataReportDetail?.antennaType?.name}
                        />

                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.HEIGHT')}
                            text={dataReportDetail?.height}
                        />

                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.WIDE')}
                            text={dataReportDetail?.wide}
                        />

                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.DOOR')}
                            text={dataReportDetail?.door?.name}
                        />

                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.LOCATION')}
                            text={`${dataReportDetail?.tambol} ${dataReportDetail?.amphur} ${dataReportDetail?.province}`}
                        />

                        <ReportItemDetailLabel
                            label={t('LABEL.MAP.REPORT_FEEDBACK.COMMENT_DESC')}
                            text={dataReportDetail?.otherDetail}
                        />
                    </Stack>
                </StyledContent>
            </StyledContainer>
        </Drawer>
    )

    return (
        <React.Fragment>
            {DrawerFormReportFeedback}
            {DrawerFormReportFeedbackDetail}
            <UnblockPopupDetail isOpen={openIosHelp} onClose={onClickCloseIosHelp} />
            <Dialog
                type="ok"
                severity="error"
                title={t('ALERT.TITLE.ERROR')}
                content={t('ALERT.CONTENT.NOT_RETRIEVE_DATA')}
                isOpen={openDialogError}
                onClickOk={handleCloseErrorDialog}
                labelButtonOk={t('BUTTON.OK')}
                disableClickBackdrop
                hideCloseButton
            />
        </React.Fragment>
    )
}

export default observer(FormReportFeedback)

const useStyles = makeStyles((theme: Theme) => ({
    drawerPaper: {
        overflow: 'hidden !important',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    container: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    backIcon: {
        color: `${theme.palette.text.secondary} !important`,
    },
    divButtonGroup: {
        display: 'flex',
        justifyContent: 'end',
        paddingBottom: '15px',
    },
    divButton: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginLeft: theme.spacing(1),
    },
    button: {
        width: '125px',
        minHeight: '37px',
    },
    link: {
        color: '#2367b4',
        textDecoration: 'underline',
        '&:hover': {
            color: '#1d5494',
            cursor: 'pointer',
        },
    },
    textDetailHead: {
        color: '#000',
        fontSize: '18px',
        fontWeight: 600,
    },
    textDetailBody: {
        color: '#0F192C',
        fontSize: '16px',
        fontWeight: 400,
    },
    iconColor: {
        '& path': {
            fill: '#fff',
        },
    },
}))

const StyledContainer = styled('div')(({ theme }) => ({
    width: '350px',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
        width: '100%',
    },
}))

const StyledTitle = styled('div')(({ theme }) => ({
    width: '100%',
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0px',
    backgroundColor: theme.palette.secondary.main,
    boxShadow: theme.palette.getContrastText('shadow-bottom'),
}))

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    textAlign: 'center',
}))

const StyledContent = styled('div')(({ theme }) => ({
    display: 'flex',
    margin: '20px',
}))

const StyledContentReport = styled('div')(({ theme }) => ({
    display: 'flex',
    height: `calc(100% - 125px)`,
}))

const StyledIconInfo = styled(InfoIcon)(({ theme }) => ({
    fontSize: '15px',
}))

const StyledTypoIosRemark = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
}))
